import { zodResolver } from "@hookform/resolvers/zod";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useForm } from "react-hook-form";

import { FormField } from "@/components/FormField";
import { api, ApiError, errorMessage, unwrapEmpty } from "@/lib/api";

import { changePasswordSchema, type ChangePasswordValues } from "./changePasswordSchema";

const EMPTY: ChangePasswordValues = {
  current_password: "",
  new_password: "",
  confirm_password: "",
};

/** 後端會回欄位錯誤的兩個欄位；確認欄位只存在於前端 */
const SERVER_FIELDS = ["current_password", "new_password"] as const;

/**
 * 修改自己的密碼。
 *
 * 成功後**這台裝置也會被登出**：`POST /me/password/` 會作廢這個帳號所有的
 * refresh token（`core.security.revoke_all_sessions`），包含發出請求的這一台。
 * 沒有改成「保留目前裝置」是刻意的 —— 那需要後端在同一個請求裡簽發新 token，
 * 多一條簽發 token 的路徑；而重新登入一次正好確認使用者真的記得新密碼，
 * 不會等到十四天後 session 過期才發現打錯。所以成功時交給上層 `onChanged`
 * 清掉本地 session、帶著提示回登入頁。
 */
export function ChangePasswordDialog({
  open,
  onClose,
  onChanged,
}: {
  open: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordValues>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: EMPTY,
  });

  const onSubmit = async (values: ChangePasswordValues) => {
    setSubmitError(null);
    try {
      unwrapEmpty(
        await api.POST("/me/password/", {
          body: {
            current_password: values.current_password,
            new_password: values.new_password,
          },
        }),
      );
      onChanged();
    } catch (error) {
      // 節流的 429 帶的是 DRF 的英文 detail，換成中文（登入頁是同一個處理）
      if (error instanceof ApiError && error.status === 429) {
        setSubmitError("嘗試次數過多，請稍候一分鐘再試。");
        return;
      }
      const fields = error instanceof ApiError ? error.fieldErrors : {};
      let marked = false;
      for (const field of SERVER_FIELDS) {
        if (fields[field]) {
          setError(field, { message: fields[field] });
          marked = true;
        }
      }
      // 欄位錯誤已經標在欄位上，上方再放一條通用訊息只是重複
      if (!marked) setSubmitError(errorMessage(error, "修改密碼失敗。"));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      // 關閉動畫結束才清空，不然欄位會在淡出時先閃成空白
      slotProps={{
        transition: {
          onExited: () => {
            reset(EMPTY);
            setSubmitError(null);
          },
        },
      }}
    >
      <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} noValidate>
        <DialogTitle>修改密碼</DialogTitle>
        <DialogContent>
          <Stack spacing={2.25}>
            <Typography variant="body2" color="text.secondary">
              修改後所有裝置（包含這一台）都會登出，請用新密碼重新登入。
            </Typography>

            {submitError && <Alert severity="error">{submitError}</Alert>}

            <FormField id="current-password" label="目前密碼">
              <TextField
                {...register("current_password")}
                id="current-password"
                type="password"
                autoComplete="current-password"
                autoFocus
                fullWidth
                error={Boolean(errors.current_password)}
                helperText={errors.current_password?.message}
              />
            </FormField>

            <FormField id="new-password" label="新密碼">
              <TextField
                {...register("new_password")}
                id="new-password"
                type="password"
                autoComplete="new-password"
                fullWidth
                error={Boolean(errors.new_password)}
                helperText={errors.new_password?.message}
              />
            </FormField>

            <FormField id="confirm-password" label="確認新密碼">
              <TextField
                {...register("confirm_password")}
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                fullWidth
                error={Boolean(errors.confirm_password)}
                helperText={errors.confirm_password?.message}
              />
            </FormField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose} disabled={isSubmitting}>
            取消
          </Button>
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {isSubmitting ? "更新中…" : "更新密碼"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
